import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import api from "@/lib/api";
import Seo from "@/components/Seo";

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" }) : "Date TBA";
const fmtTime = (iso) => iso ? new Date(iso).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" }) : "";
const STATUS = { scheduled: "", completed: "Completed", no_show: "No-show" };

export default function Roster() {
  const { sessionId } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get(`/admin/sessions/${sessionId}/roster`).then(({ data }) => setD(data)).catch((e) => setErr(e?.response?.data?.detail || "Could not load roster"));
  }, [sessionId]);

  if (err) return <div className="min-h-screen bg-white text-black p-10 font-mono" data-testid="roster-error">{err}</div>;
  if (!d) return <div className="min-h-screen bg-white text-black p-10 font-mono">Loading roster…</div>;
  const { session: s, course: c, roster, totals } = d;
  const blanks = Math.max(0, s.capacity - roster.length);

  return (
    <div className="min-h-screen bg-white text-black" data-testid="roster-page">
      <Seo title={`Sign-in Sheet — ${c.title}`} path={`/admin/roster/${sessionId}`} noindex />
      <style>{`@media print { .no-print { display: none !important; } @page { size: letter; margin: 0.5in; } body { background: #fff; } }`}</style>

      <div className="no-print flex items-center justify-between gap-3 px-8 py-3 bg-neutral-100 border-b border-neutral-300">
        <Link to="/admin" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-neutral-700 hover:text-black" data-testid="roster-back"><ArrowLeft size={14} /> Back to admin</Link>
        <button onClick={() => window.print()} className="inline-flex items-center gap-2 bg-black text-[#FFD300] font-mono text-xs font-bold uppercase tracking-[0.14em] px-4 py-2" data-testid="roster-print-btn"><Printer size={14} /> Print / Save PDF</button>
      </div>

      <div className="max-w-[8.5in] mx-auto px-8 py-8">
        <header className="flex items-start justify-between border-b-2 border-black pb-4 mb-5">
          <div>
            <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-neutral-600">Secrecy Executive Protection · Training Academy</div>
            <h1 className="font-heading text-3xl tracking-tight mt-1" data-testid="roster-title">Class Sign-In Sheet</h1>
            <div className="text-lg font-semibold mt-2" data-testid="roster-course">{c.title}</div>
            <div className="font-mono text-xs text-neutral-700 mt-0.5">{c.code}{c.state ? ` · ${c.state}` : ""} · ${Number(c.price || 0).toFixed(0)}</div>
          </div>
          <div className="text-right font-mono text-xs leading-6">
            <div className="font-bold text-sm" data-testid="roster-date">{fmtDate(s.starts_at)}</div>
            <div>{fmtTime(s.starts_at)}{s.ends_at ? ` – ${fmtTime(s.ends_at)}` : ""}</div>
            <div>{s.venue || "Venue TBA"}</div>
            <div>Instructor: {s.instructor || "—"}</div>
            <div data-testid="roster-count">{roster.length} / {s.capacity} seats</div>
          </div>
        </header>

        <table className="w-full border-collapse text-sm" data-testid="roster-table">
          <thead>
            <tr className="bg-black text-white font-mono text-[10px] uppercase tracking-[0.14em]">
              <th className="p-2 text-left w-8">#</th>
              <th className="p-2 text-left">Student</th>
              <th className="p-2 text-left">Phone</th>
              <th className="p-2 text-left">Payment</th>
              <th className="p-2 text-left w-20">Photo ID</th>
              <th className="p-2 text-left w-40">Signature</th>
            </tr>
          </thead>
          <tbody>
            {roster.map((r, i) => (
              <tr key={r.enrollment_id} className="border-b border-neutral-400 align-top" data-testid={`roster-row-${r.enrollment_id}`}>
                <td className="p-2 font-mono text-xs">{i + 1}</td>
                <td className="p-2">
                  <div className="font-semibold">{r.name}{STATUS[r.status] ? <span className="ml-2 font-mono text-[10px] uppercase text-neutral-500">{STATUS[r.status]}</span> : null}</div>
                  <div className="font-mono text-[11px] text-neutral-600">{r.email}</div>
                  {r.note && <div className="text-[11px] text-neutral-500 italic">{r.note}</div>}
                </td>
                <td className="p-2 font-mono text-xs">{r.phone || "—"}</td>
                <td className={`p-2 font-mono text-xs font-bold ${r.balance_due_cents > 0 ? "text-red-700" : "text-emerald-700"}`} data-testid={`roster-pay-${r.enrollment_id}`}>{r.payment}</td>
                <td className="p-2"><div className="h-7 border border-neutral-400 w-5 inline-block align-middle" /></td>
                <td className="p-2"><div className="h-7 border-b border-neutral-700" /></td>
              </tr>
            ))}
            {Array.from({ length: blanks }).map((_, i) => (
              <tr key={`blank-${i}`} className="border-b border-neutral-300 text-neutral-400">
                <td className="p-2 font-mono text-xs">{roster.length + i + 1}</td>
                <td className="p-2"><div className="h-7 border-b border-neutral-300" /></td>
                <td className="p-2"><div className="h-7 border-b border-neutral-300" /></td>
                <td className="p-2"><div className="h-7 border-b border-neutral-300" /></td>
                <td className="p-2"><div className="h-7 border border-neutral-300 w-5 inline-block" /></td>
                <td className="p-2"><div className="h-7 border-b border-neutral-300" /></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-between mt-5 font-mono text-xs" data-testid="roster-totals">
          <div>{totals.students} enrolled · <span className={totals.owing ? "text-red-700 font-bold" : ""}>{totals.owing} with balance due{totals.owing ? ` · $${(totals.outstanding_cents / 100).toFixed(2)} outstanding` : ""}</span></div>
          <div className="text-neutral-500">Printed {new Date(d.generated_at).toLocaleString("en-US")}</div>
        </div>

        <div className="grid grid-cols-2 gap-10 mt-10 text-xs font-mono">
          <div><div className="border-b border-black h-8" /><div className="mt-1 text-neutral-600">Instructor signature</div></div>
          <div><div className="border-b border-black h-8" /><div className="mt-1 text-neutral-600">Date</div></div>
        </div>
        <p className="mt-6 text-[10px] text-neutral-500 leading-relaxed">Students must present a valid government photo ID. Balances marked OWES must be collected before the student is admitted to live-fire. Collected balances should be recorded in Admin → Signups → "Paid in person".</p>
      </div>
    </div>
  );
}
